import React, { useEffect } from 'react'

import { Box, Button, useToast } from '@chakra-ui/react'

import api, { axios } from '@/api'
import { RefreshIcon } from '@/views/assets/RefreshIcon'

import { IAppVersionContext } from './types'

export const AppVersionContext = React.createContext<IAppVersionContext>(
  {} as IAppVersionContext,
)

export const AppVersionProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const [version, setVersion] = React.useState('')
  const [isUpdateNeeded, setIsUpdateNeeded] = React.useState(false)

  const toast = useToast()

  const onUpdateNeeded = () => {
    if (isUpdateNeeded && !toast.isActive('version-update-needed')) {
      toast({
        containerStyle: {
          minW: 0,
        },
        id: 'version-update-needed',
        render: () => <ReloadPageToast />,
        duration: null,
        isClosable: true,
      })
    }
  }

  useEffect(() => {
    onUpdateNeeded()
  }, [isUpdateNeeded])

  useEffect(() => {
    const id = axios.interceptors.response.use(function (response) {
      const responseVersion = response.headers['x-app-version']
      if (!responseVersion) {
        return response
      }

      if (!version) {
        setVersion(responseVersion)
        return response
      }

      if (version !== responseVersion) {
        console.log(
          'New version detected',
          'old:',
          version,
          'new:',
          responseVersion,
        )
        setIsUpdateNeeded(true)
      }
      return response
    })
    return () => {
      axios.interceptors.response.eject(id)
    }
  }, [version])

  useEffect(() => {
    // periodically send a request to the server to check for a new version
    const interval = setInterval(() => {
      api.checkVersion().catch(console.error)
    }, 1000 * 30)

    // if the version is updated, stop the interval.
    if (isUpdateNeeded) {
      clearInterval(interval)
    }

    return () => {
      clearInterval(interval)
    }
  }, [isUpdateNeeded])

  return (
    <AppVersionContext
      value={{
        version,
        setVersion,
      }}
    >
      {children}
    </AppVersionContext>
  )
}

const ReloadPageToast = () => {
  return (
    <Box
      bg={'transparent'}
      color={'white'}
      fontWeight={500}
      mb={2}
      rounded={'2.5rem'}
      boxShadow={'0px 1px 10px 0px #8B77FB'}
    >
      <Button
        bg={'#8B77FB'}
        _hover={{ bg: '#7b63ff' }}
        transition={'background-color 0.2s'}
        rounded={'2.5rem'}
        py={2}
        variant={'unstyled'}
        display={'flex'}
        alignItems={'center'}
        w={'100%'}
        h={'100%'}
        px={4}
        leftIcon={<RefreshIcon mr={1} w={'17px'} h={'22px'} fill="white" />}
        onClick={() => {
          location.reload()
        }}
      >
        Please refresh the page to get the new version
      </Button>
    </Box>
  )
}
